import { useCallback, useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { PlusCircledIcon } from '@radix-ui/react-icons'
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, type Row } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { proxy, useSnapshot } from 'valtio'
import { z } from 'zod'

import { Trash } from '@/components/icons'
import { ActionMenu } from '@/components/ui/action-menu'
import { Button } from '@/components/ui/button'
import { Drawer } from '@/components/ui/drawer'
import { Form, FormFieldset } from '@/components/ui/form'
import { InputCombobox } from '@/components/ui/input-combobox'
import { PageCard, PageLayout } from '@/components/ui/page'
import { usePrompt } from '@/components/ui/prompt'
import {
	CellText,
	DataTable,
	useDataTable,
} from '@/components/ui/table/data-table'
import { toast } from '@/components/ui/toaster/util'

import { invalidateQueries } from '@/libs/helper'
import { useAuth } from '@/libs/hooks/use-auth'

import {
	accountMasterList,
	accountStaffAdd,
	accountStaffDelete,
	accountStaffList,
} from '@/features/account/api'
import type { IAccount } from '@/features/account/type'
import { authzTeamList } from '@/features/authz/api'

export const Route = createFileRoute('/_app/outlet/staff/')({
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout>
			<PageLayout.Header
				breadcrumbs={[
					['/outlet', 'Outlet'],
					['/outlet/staff', 'Staff'],
				]}
			/>
			<StaffTableSection />
		</PageLayout>
	)
}

const state = proxy({ drawerAddOpen: false })

function StaffTableSection() {
	return (
		<PageCard>
			<PageCard.Header
				title="Daftar Staff"
				actions={
					<PageCard.HeaderAction onClick={() => (state.drawerAddOpen = true)}>
						<PlusCircledIcon />
						Tambah
					</PageCard.HeaderAction>
				}
			/>
			<DrawerAddStaff />
			<StaffTable />
		</PageCard>
	)
}

function StaffTable() {
	const { outletId } = useAuth((s) => s.outlet)
	const { data, isLoading } = useQuery(accountStaffList.qry({ oId: outletId }))
	const columns = useMemo(getColumnsStaff, [])
	const { table } = useDataTable({
		columns,
		data: data?.accounts ?? [],
	})

	return <DataTable table={table} columns={columns} isLoading={isLoading} />
}

const chStaff = createColumnHelper<IAccount>()
function getColumnsStaff() {
	return [
		chStaff.accessor('name', {
			header: 'Name',
			cell: ({ row }) => <CellText text={row.original.name} />,
		}),
		chStaff.accessor('email', {
			header: 'Email',
			cell: ({ row }) => <CellText text={row.original.email} />,
		}),
		chStaff.display({
			id: 'team',
			header: 'Team',
			cell: ({ row }) => <CellText text={row.original.teams[0].tId} />,
		}),
		chStaff.display({
			id: 'actions',
			header: 'Actions',
			cell: ({ row }) => <StaffActions row={row} />,
		}),
	]
}

function StaffActions({ row }: { row: Row<IAccount> }) {
	const prompt = usePrompt()
	const { outletId } = useAuth((s) => s.outlet)
	const { mutateAsync } = useMutation({
		mutationFn: async () => {
			await accountStaffDelete.api({
				body: {
					oId: outletId,
					accountIds: [row.original.id],
				},
			})
		},
		onSuccess: () => {
			state.drawerAddOpen = false
			invalidateQueries([accountMasterList.key, accountStaffList.key])
		},
	})

	const handleDelete = async () => {
		const res = await prompt({
			title: 'Hapus Staff',
			description: 'Apakah anda yakin ingin menghapus staff ini?',
			confirmText: 'Hapus',
			cancelText: 'Batal',
		})

		if (!res) return

		toast.promise(mutateAsync(), {
			loading: 'Menghapus Staff...',
			success: 'Staff berhasil dihapus',
			error: 'Gagal menghapus Staff',
		})
	}

	return (
		<ActionMenu
			groups={[
				{
					actions: [
						{
							icon: <Trash />,
							label: 'Hapus',
							onClick: handleDelete,
						},
					],
				},
			]}
		/>
	)
}

type AddStaffFormDto = z.infer<typeof AddStaffFormDto>
const AddStaffFormDto = z.object({
	teamId: z.string().min(1),
	accountId: z.string().min(1),
})

function DrawerAddStaff() {
	const { outletId } = useAuth((s) => s.outlet)
	const open = useSnapshot(state).drawerAddOpen

	const teamQry = useSuspenseQuery(authzTeamList.qry())
	const accountsQry = useSuspenseQuery(accountMasterList.qry())
	const staffAlreadyAddedQry = useSuspenseQuery(
		accountStaffList.qry({ oId: outletId })
	).data.accounts.map((a) => a.id)

	const form = useForm<AddStaffFormDto>({
		defaultValues: { accountId: undefined!, teamId: undefined! },
		resolver: zodResolver(AddStaffFormDto),
	})

	const availableAccounts = useMemo(() => {
		return accountsQry.data.accounts.filter(
			(acc) => acc.role !== 'admin' && !staffAlreadyAddedQry.includes(acc.id)
		)
	}, [accountsQry.data.accounts, outletId])

	const { mutateAsync, isPending } = useMutation({
		mutationFn: async (data: AddStaffFormDto) => {
			await accountStaffAdd.api({
				body: {
					oId: outletId,
					teamId: data.teamId,
					accountIds: [data.accountId],
				},
			})
		},
		onSuccess: () => {
			state.drawerAddOpen = false
			resetForm()
			invalidateQueries([accountMasterList.key, accountStaffList.key])
		},
	})

	const handleSubmit = form.handleSubmit(async (data) => {
		toast.promise(mutateAsync(data), {
			loading: 'Menambahkan Staff...',
			success: 'Staff berhasil ditambahkan',
			error: 'Gagal menambahkan Staff',
		})
	})

	const resetForm = useCallback(() => {
		form.reset({ teamId: undefined!, accountId: undefined })
	}, [form.reset])

	useEffect(() => {
		if (!open) resetForm()
	}, [open, resetForm])

	return (
		<Drawer open={open} onOpenChange={(v) => (state.drawerAddOpen = v)}>
			<Drawer.Content>
				<Form {...form}>
					<Drawer.Header>
						<Drawer.Title>Tambah Staff</Drawer.Title>
					</Drawer.Header>
					<Drawer.Body>
						<div className="flex flex-col gap-3">
							<Form.Field
								control={form.control}
								name="teamId"
								render={({ field }) => (
									<FormFieldset.One label="Pilih Team">
										<InputCombobox
											options={teamQry.data.teams.map((x) => ({
												label: x.name,
												value: x.id,
												disabled: false,
											}))}
											value={field.value}
											onChange={field.onChange}
										/>
									</FormFieldset.One>
								)}
							/>
							<Form.Field
								control={form.control}
								name="accountId"
								render={({ field }) => (
									<FormFieldset.One label="Pilih Staff">
										<InputCombobox
											options={availableAccounts.map((acc) => ({
												label: acc.name,
												value: acc.id,
												disabled: false,
											}))}
											placeholder="Pilih Staff"
											value={field.value}
											onChange={field.onChange}
										/>
									</FormFieldset.One>
								)}
							/>
						</div>
					</Drawer.Body>
					<Drawer.Footer>
						<Drawer.Close asChild>
							<Button variant="secondary">Batal</Button>
						</Drawer.Close>
						<Button onClick={handleSubmit} isLoading={isPending}>
							Simpan
						</Button>
					</Drawer.Footer>
				</Form>
			</Drawer.Content>
		</Drawer>
	)
}
